// src/app/(dashboard)/meter/components/MeterPagination.tsx
"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";

interface MeterPaginationProps {
  page: number;
  totalPages: number;
  q: string;
  pageSize: number;
  status: string;
  meterType: string;
}

export function MeterPagination({
  page,
  totalPages,
  q,
  pageSize,
  status,
  meterType,
}: MeterPaginationProps) {
  const router = useRouter();
  const [inputValue, setInputValue] = useState(String(page));

  // Keep the input in sync when the page changes via Prev/Next or navigation
  useEffect(() => {
    setInputValue(String(page));
  }, [page]);

  const buildUrl = (targetPage: number) => {
    const qs = new URLSearchParams();
    qs.set("q", q);
    qs.set("page", String(targetPage));
    qs.set("pageSize", String(pageSize));
    if (status) qs.set("status", status);
    if (meterType) qs.set("meterType", meterType);
    return `?${qs.toString()}`;
  };

  const goToTypedPage = () => {
    const parsed = Number(inputValue);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed)) {
      setInputValue(String(page));
      return;
    }
    const clamped = Math.min(totalPages, Math.max(1, parsed));
    setInputValue(String(clamped));
    if (clamped !== page) {
      router.push(buildUrl(clamped));
    }
  };

  const buttonStyles = (disabled: boolean) =>
    [
      "flex h-9 items-center justify-center rounded-xl border px-3 text-sm font-medium leading-none transition-colors",
      "border-slate-200 bg-white text-slate-700",
      "dark:border-slate-800 dark:bg-slate-900 dark:text-slate-200",
      disabled
        ? "pointer-events-none opacity-40"
        : "hover:border-purple-300 hover:bg-purple-50 hover:text-purple-700 active:bg-purple-100 dark:hover:border-purple-700 dark:hover:bg-purple-950/40 dark:hover:text-purple-300",
    ].join(" ");

  return (
    <div className="flex items-center gap-2">
      <Link
        href={buildUrl(Math.max(1, page - 1))}
        aria-disabled={page <= 1}
        className={buttonStyles(page <= 1)}
      >
        ← ก่อนหน้า
      </Link>

      <div className="flex h-9 items-center justify-center gap-1.5 rounded-xl border border-purple-200 bg-purple-50 px-3 text-sm font-medium leading-none text-purple-700 dark:border-purple-800 dark:bg-purple-950 dark:text-purple-300">
        หน้า
        <input
          type="text"
          inputMode="numeric"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value.replace(/[^0-9]/g, ""))}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              goToTypedPage();
              (e.target as HTMLInputElement).blur();
            }
          }}
          onBlur={goToTypedPage}
          aria-label="ไปยังหน้า"
          className="w-8 rounded-md border-0 bg-transparent text-center text-purple-700 outline-none focus:ring-1 focus:ring-purple-400 dark:text-purple-300"
        />
        / {totalPages}
      </div>

      <Link
        href={buildUrl(Math.min(totalPages, page + 1))}
        aria-disabled={page >= totalPages}
        className={buttonStyles(page >= totalPages)}
      >
        ถัดไป →
      </Link>
    </div>
  );
}
